import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, 
  Shield, 
  User,
  Loader2,
  Building2,
  UserX,
  UserMinus,
  UserCog,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserManagement() {
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [pendingManagers, setPendingManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userToRemove, setUserToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [processingApproval, setProcessingApproval] = useState(null);

  useEffect(() => {
    fetchUsers();
    if (currentUser?.company_id) {
      fetchCompanyUsers();
      if (isAdmin()) {
        fetchPendingManagers();
      }
    }
  }, [currentUser]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyUsers = async () => {
    if (!currentUser?.company_id) return;
    try {
      const response = await axios.get(`${API}/companies/${currentUser.company_id}/users`);
      setCompanyUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load company users');
    }
  };

  const fetchPendingManagers = async () => {
    if (!currentUser?.company_id) return;
    try {
      const response = await axios.get(`${API}/companies/${currentUser.company_id}/pending-managers`);
      setPendingManagers(response.data.pending_managers || []);
    } catch (error) {
      console.error('Failed to load pending managers');
    }
  };

  const handleApproveManager = async (userId) => {
    setProcessingApproval(userId);
    try {
      await axios.post(`${API}/companies/${currentUser.company_id}/approve-manager/${userId}`);
      toast.success('Manager approved!');
      setPendingManagers(prev => prev.filter(u => u.user_id !== userId));
      fetchCompanyUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve manager');
    } finally {
      setProcessingApproval(null);
    }
  };

  const handleRejectManager = async (userId) => {
    setProcessingApproval(userId);
    try {
      await axios.post(`${API}/companies/${currentUser.company_id}/reject-manager/${userId}`);
      toast.success('Manager rejected');
      setPendingManagers(prev => prev.filter(u => u.user_id !== userId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject manager');
    } finally {
      setProcessingApproval(null);
    }
  };

  const handleRemoveFromCompany = async () => {
    if (!userToRemove || !currentUser?.company_id) return;
    
    setRemoving(true);
    try {
      await axios.delete(`${API}/companies/${currentUser.company_id}/users/${userToRemove.user_id}`);
      toast.success(`${userToRemove.name} removed from company`);
      setCompanyUsers(prev => prev.filter(u => u.user_id !== userToRemove.user_id));
      setUserToRemove(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    } finally {
      setRemoving(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return (
          <Badge className="bg-primary">
            <Shield className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        );
      case 'manager':
        return (
          <Badge variant="secondary" className="bg-blue-100 text-blue-700">
            <UserCog className="h-3 w-3 mr-1" />
            Manager
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            <User className="h-3 w-3 mr-1" />
            Operative
          </Badge>
        );
    }
  };

  const stats = {
    total: companyUsers.length,
    admins: companyUsers.filter(u => u.role === 'admin').length,
    managers: companyUsers.filter(u => u.role === 'manager').length,
    users: companyUsers.filter(u => u.role === 'user').length,
    pending: pendingManagers.length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="users-page">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-foreground">
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            {currentUser?.company_name ? `Manage users in ${currentUser.company_name}` : 'Manage user accounts'}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="text-xl font-bold">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Admin</p>
                  <p className="text-xl font-bold">{stats.admins}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <UserCog className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Managers</p>
                  <p className="text-xl font-bold">{stats.managers}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Operatives</p>
                  <p className="text-xl font-bold">{stats.users}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {isAdmin() && (
            <Card className={stats.pending > 0 ? 'border-amber-300' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Pending</p>
                    <p className="text-xl font-bold">{stats.pending}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Pending Manager Approvals (Admin only) */}
        {isAdmin() && pendingManagers.length > 0 && (
          <Card className="mb-8 border-amber-300">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-amber-700">
                <Clock className="h-5 w-5" />
                Pending Manager Approvals
              </CardTitle>
              <CardDescription>
                These users have requested manager access to your company
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingManagers.map(user => (
                  <div 
                    key={user.user_id} 
                    className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200"
                  >
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleApproveManager(user.user_id)}
                        disabled={processingApproval === user.user_id}
                        data-testid={`approve-manager-${user.user_id}`}
                      >
                        {processingApproval === user.user_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => handleRejectManager(user.user_id)}
                        disabled={processingApproval === user.user_id}
                        data-testid={`reject-manager-${user.user_id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Company Users Table */}
        {currentUser?.company_id && companyUsers.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    {currentUser.company_name || 'Company'} Team
                  </CardTitle>
                  <CardDescription>
                    {isAdmin() 
                      ? 'You can remove any user or manager from your company'
                      : 'View your company team members'
                    }
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      {isAdmin() && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companyUsers.map(user => (
                      <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                        <TableCell className="font-medium">
                          {user.name}
                          {user.user_id === currentUser.user_id && (
                            <span className="text-xs text-muted-foreground ml-2">(You)</span>
                          )}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{getRoleBadge(user.role)}</TableCell>
                        <TableCell>
                          {user.admin_status === 'pending_approval' ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              Pending
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-green-600 border-green-300">
                              Active
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(user.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        {isAdmin() && (
                          <TableCell>
                            {user.user_id !== currentUser.user_id ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setUserToRemove(user)}
                                data-testid={`remove-user-${user.user_id}`}
                              >
                                <UserMinus className="h-4 w-4 mr-1" />
                                Remove
                              </Button>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {!currentUser?.company_id && (
          <Card className="text-center py-12">
            <CardContent>
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">No Company</h3>
              <p className="text-muted-foreground">
                You're not part of any company. Join or create a company to manage users.
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Remove User Confirmation Dialog */}
      <AlertDialog open={!!userToRemove} onOpenChange={() => setUserToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-red-500" />
              Remove User from Company
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{userToRemove?.name}</strong> from your company? 
              They will become an individual user and will only see their own forms.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFromCompany}
              disabled={removing}
              className="bg-red-600 hover:bg-red-700"
            >
              {removing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <UserMinus className="h-4 w-4 mr-2" />
              )}
              Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
